import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import type {
  MappingExpandResponse,
  HubSpotCatalogProduct,
  HubSpotLineItemDetail,
  StripeCatalogPrice,
  StripeCatalogMeter,
} from '@/types/api';

export type MappingKind = 'product' | 'meter' | 'override';

interface MappingDetailPanelProps {
  kind: MappingKind;
  hubspotId: string;
  stripeId?: string | null;
}

function formatStripeAmount(unitAmount?: number | null, currency?: string | null): string {
  if (unitAmount == null) return '—';
  const code = (currency || 'usd').toUpperCase();
  return `${(unitAmount / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })} ${code}`;
}

function DetailField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 text-sm py-0.5">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="text-foreground text-right truncate">{value ?? '—'}</span>
    </div>
  );
}

function SideCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border bg-background p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
        {title}
      </p>
      {children}
    </div>
  );
}

function NotFound({ label }: { label: string }) {
  return <p className="text-sm text-muted-foreground italic">{label} not found</p>;
}

function HubSpotProductDetails({ product }: { product: HubSpotCatalogProduct }) {
  return (
    <div>
      <DetailField label="Name" value={product.name} />
      <DetailField label="Description" value={product.description} />
      <DetailField
        label="Price"
        value={product.price != null ? product.price.toLocaleString() : undefined}
      />
      <DetailField label="Billing frequency" value={product.recurring_billing_frequency} />
    </div>
  );
}

function HubSpotLineItemDetails({ lineItem }: { lineItem: HubSpotLineItemDetail }) {
  return (
    <div>
      <DetailField label="Name" value={lineItem.name} />
      <DetailField label="Description" value={lineItem.description} />
      <DetailField
        label="Price"
        value={lineItem.price != null ? lineItem.price.toLocaleString() : undefined}
      />
      <DetailField label="Quantity" value={lineItem.quantity} />
      <DetailField
        label="Amount"
        value={lineItem.amount != null ? lineItem.amount.toLocaleString() : undefined}
      />
      <DetailField label="Product ID" value={lineItem.hs_product_id} />
      <DetailField label="Billing period" value={lineItem.recurring_billing_period} />
    </div>
  );
}

function StripePriceDetails({ price }: { price: StripeCatalogPrice }) {
  return (
    <div>
      <DetailField label="Product" value={price.product_name ?? price.product_id} />
      <DetailField label="Nickname" value={price.nickname} />
      <DetailField label="Amount" value={formatStripeAmount(price.unit_amount, price.currency)} />
      <DetailField
        label="Interval"
        value={
          price.interval
            ? `every ${price.interval_count && price.interval_count > 1 ? `${price.interval_count} ` : ''}${price.interval}${price.interval_count && price.interval_count > 1 ? 's' : ''}`
            : 'one-time'
        }
      />
      <DetailField
        label="Status"
        value={
          <Badge variant={price.active ? 'secondary' : 'outline'}>
            {price.active ? 'Active' : 'Inactive'}
          </Badge>
        }
      />
    </div>
  );
}

function StripeMeterDetails({ meter }: { meter: StripeCatalogMeter }) {
  return (
    <div>
      <DetailField label="Display name" value={meter.display_name} />
      <DetailField label="Event name" value={meter.event_name} />
      <DetailField
        label="Status"
        value={meter.status ? <Badge variant="secondary">{meter.status}</Badge> : undefined}
      />
    </div>
  );
}

/**
 * Expanded-row detail: HubSpot object on the left, Stripe object on the
 * right. Fetched lazily on first expansion and cached by react-query.
 */
const MappingDetailPanel: React.FC<MappingDetailPanelProps> = ({ kind, hubspotId, stripeId }) => {
  const { data, isLoading, isError } = useQuery<MappingExpandResponse>({
    queryKey: ['mappingExpand', kind, hubspotId, stripeId ?? null],
    queryFn: () => {
      if (kind === 'product') return apiClient.expandProductMapping(hubspotId, stripeId ?? undefined);
      if (kind === 'meter') return apiClient.expandMeterMapping(hubspotId, stripeId ?? undefined);
      return apiClient.expandOverrideMapping(hubspotId, stripeId ?? undefined);
    },
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-4 p-4">
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <p className="p-4 text-sm text-destructive">Failed to load mapping details.</p>
    );
  }

  const hubspotSide =
    kind === 'override' ? (
      data.hubspot_line_item ? (
        <HubSpotLineItemDetails lineItem={data.hubspot_line_item} />
      ) : (
        <NotFound label="HubSpot line item" />
      )
    ) : data.hubspot_product ? (
      <HubSpotProductDetails product={data.hubspot_product} />
    ) : (
      <NotFound label="HubSpot product" />
    );

  const stripeSide =
    kind === 'meter' ? (
      data.stripe_meter ? (
        <StripeMeterDetails meter={data.stripe_meter} />
      ) : stripeId ? (
        <NotFound label="Stripe meter" />
      ) : (
        <p className="text-sm text-muted-foreground italic">No Stripe meter linked</p>
      )
    ) : data.stripe_price ? (
      <StripePriceDetails price={data.stripe_price} />
    ) : stripeId ? (
      <NotFound label="Stripe price" />
    ) : (
      <p className="text-sm text-muted-foreground italic">No Stripe price linked</p>
    );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/40">
      <SideCard title="HubSpot">{hubspotSide}</SideCard>
      <SideCard title="Stripe">{stripeSide}</SideCard>
    </div>
  );
};

export default MappingDetailPanel;
